import apiOrgs from '@/api/orgs';
import apiProjects from '@/api/projects';
import apiUsers from '@/api/users';

const userState = {
    data: {
        projects: []
    },
    me: {
        name: '',
        organization: {
            organizationName: '',
            organizationId: '',
            isAuthority: false,
            isDelegate: false,
            saasId: null
        },
        organizations: [],
        userId: '',
        isEmployee: false,
        isSwAdmin: false
    },
    plan: {
        planId: '',
        targetService: ''
    },
    status: {
        accessToken: '',
        agentCode: '',
        company: '',
        email: '',
        emailVerified: false,
        expiresAt: '',
        idToken: '',
        sub: '',
        username: '',
        isAdmin: false,
        isAgency: false,
        isDisabled: false,
        isTester: false,
        isTrial: false
    }
};

const user = {
    namespaced: true,
    state: JSON.parse(JSON.stringify(userState)),
    getters: {
        hasPlan(state) {
            return state.plan.planId ? true : false;
        },
        hasOrganizations(state) {
            return state.me.organizations.length ? true : false;
        },
        hasProject(state) {
            return state.data.projects.length ? true : false;
        },
        isAgent(state) {
            return state.status.agentCode ? true : false;
        }
    },
    mutations: {
        setData(state, payload) {
            // サイトの表示名をオブジェクトに追加する
            for (let i = 0; i < payload.projects.length; i++) {
                let project = payload.projects[i];
                let displayName = '';
                if (project.organizationName) {
                    displayName = `${project.organizationName} ${project.name}`;
                } else {
                    displayName = project.name;
                }
                project.displayName = displayName;
            }
            Object.assign(state.data, payload);
        },
        setMe(state, payload) {
            Object.assign(state.me, payload);
        },
        setPlan(state, payload) {
            Object.assign(state.plan, payload);
        },
        setProject(state, payload) {
            if (!payload) {
                return;
            }
            const index = state.data.projects.findIndex(project => {
                return project.id === payload.id;
            });
            // サイトの表示名をオブジェクトに追加する
            let displayName = '';
            if (payload.organizationName) {
                displayName = `${payload.organizationName} ${payload.name}`;
            } else {
                displayName = payload.name;
            }
            payload.displayName = displayName;
            if (index !== -1) {
                state.data.projects.splice(index, 1, payload);
            } else {
                state.data.projects.push(payload);
            }
        },
        setStatus(state, payload) {
            Object.assign(state.status, payload);
        },
        reset(state) {
            Object.assign(state.data, userState.data);
            Object.assign(state.me, userState.me);
            Object.assign(state.plan, userState.plan);
            Object.assign(state.status, userState.status);
        },
        resetData(state) {
            Object.assign(state.data, userState.data);
        }
    },
    actions: {
        async getMe({ commit, state }) {
            const me = await apiUsers.me
                .get(state.status.idToken)
                .then(response => {
                    // ユーザーが複数の組織に属しているかどうかで返ってくるkeyが違うため（organization or organizations）、初期値と返り値を合体させる
                    // organizationとorganizationsの両方にデータがあるという状態を避けたい
                    return Object.assign({}, userState.me, response.data);
                })
                .catch(error => {
                    // 403の場合は無効なアカウントとして扱う
                    if (error.response.status === 403) {
                        commit('setStatus', { isDisabled: true });
                    }
                    return userState.me; //初期値に戻す
                });
            commit('setMe', me);
        },
        async getPlan({ commit, state }) {
            const plan = await apiOrgs.subscriptions
                .get(state.status.idToken)
                .then(response => {
                    const base = response.data.subscriptions.base;
                    if (base.length) {
                        return {
                            planId: base[0].planId,
                            targetService: base[0].targetService
                        };
                    } else {
                        return userState.plan; //初期値に戻す
                    }
                })
                .catch(() => userState.plan); //初期値に戻す
            commit('setPlan', plan);
        },
        async getProjects({ commit, state, rootGetters }) {
            const organizationId =
                rootGetters['view/organization'].organizationId;

            if (!organizationId) {
                return;
            }
            const params = {
                organizationId: organizationId
            };
            const projects = await apiProjects
                .get(state.status.idToken, params)
                .then(response => {
                    commit('setStatus', { isDisabled: false });
                    return response.data.items;
                })
                .catch(error => {
                    if (
                        error.response &&
                        error.response.data &&
                        error.response.data.errors[0].status === 403 &&
                        error.response.data.errors[0].code === '901'
                    ) {
                        commit('setStatus', { isDisabled: true });
                    } else {
                        commit('setStatus', { isDisabled: false });
                    }
                    return [];
                });
            commit('setData', { projects: projects });
        },
        async getProject({ commit, state }, { projectId }) {
            if (!projectId) {
                return;
            }
            const project = await apiProjects.project
                .get(state.status.idToken, projectId)
                .then(response => {
                    return response.data;
                })
                .catch(error => {
                    return null;
                });
            commit('setProject', project);
        },
        setData({ commit }, response) {
            commit('setData', response);
        },
        setPlan({ commit }, response) {
            commit('setPlan', response);
        },
        setStatus({ commit }, response) {
            commit('setStatus', response);
        },
        reset({ commit }) {
            commit('reset');
        },
        resetData({ commit }) {
            commit('resetData');
        }
    }
};

export default user;
